import React, { useState } from 'react';
import { Box, Flex, Text, Collapse } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface CaregiversInformationProps {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

const CaregiversInformation: React.FC<CaregiversInformationProps> = ({
  isOpen: controlledIsOpen,
  onToggle,
}) => {
  const { t } = useTranslation();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    if (controlledIsOpen !== undefined && onToggle) {
      onToggle(newIsOpen);
    } else {
      setInternalIsOpen(newIsOpen);
    }
  };

  return (
    <Box width="100%">
      {/* Button - Only show when closed */}
      {!isOpen && (
        <Box
          as="button"
          bg="#F3F2F2"
          borderRadius="20px"
          pl="15px"
          pr="7.5px"
          py="0"
          border="none"
          cursor="pointer"
          onClick={handleToggle}
          display="inline-flex"
          alignItems="center"
          height="40px"
          _hover={{ bg: '#E8E7E7' }}
          _active={{ bg: '#DDDCDC' }}
        >
          <Flex
            justifyContent="flex-start"
            alignItems="center"
            gap="8px"
          >
            <Text
              fontSize="15px"
              fontWeight={300}
              color="#000"
            >
              {t('caregiverInfo')}
            </Text>
            <Box
              width="25px"
              height="25px"
              borderRadius="50px"
              bg="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="11"
                viewBox="0 0 11 11"
                fill="none"
              >
                <path
                  d="M6.21582 9.67338L0.50045 9.67338L0.50045 3.95801"
                  stroke="#2B4956"
                />
                <path
                  d="M3.85449 0.50045L9.56986 0.50045L9.56986 6.21582"
                  stroke="#2B4956"
                />
              </svg>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Info Box - Only show when open */}
      <Collapse in={isOpen} animateOpacity>
        <Box
          bg="#F3F2F2"
          borderRadius="20px"
          p="25px"
          border="1px solid #DCDCDC"
          width="100%"
          position="relative"
        >
          {/* Expand icon - clickable to close */}
          <Box
            as="button"
            onClick={handleToggle}
            position="absolute"
            top="15px"
            right="15px"
            width="25px"
            height="25px"
            borderRadius="50px"
            bg="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            _active={{ bg: 'gray.100' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
            >
              <path d="M0 8.46236H5.71537V14.1777" stroke="#2B4956" />
              <path d="M14.0664 5.71537H8.35104V0" stroke="#2B4956" />
            </svg>
          </Box>
          <Text fontSize="15px" fontWeight={300} color="#2B4956" mb="12px" mr="30px">
            {t('caregiverInfoText1')}
          </Text>
          
          <Text fontSize="15px" fontWeight={300} color="#2B4956" mb="12px">
            {t('caregiverInfoText2')}{' '}
            <Box
              as="span"
              border="1px solid"
              borderColor="alea.500"
              borderRadius="10px"
              px="7px"
              py="5px"
            >
              {t('username')}
            </Box>
            <Text as="span" mx="6px">
              +
            </Text>
            <Box
              as="span"
              border="1px solid"
              borderColor="alea.500"
              borderRadius="10px"
              px="7px"
              py="5px"
            >
              {t('careHomeID')}
            </Box>
          </Text>

          <Text fontSize="15px" fontWeight={500} color="black" mb="12px">
            {t('caregiverInfoText3')}
          </Text>
          
          <Text fontSize="15px" fontWeight={300} color="#2B4956">
            {t('caregiverInfoText4')}
          </Text>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CaregiversInformation;

